import { useState } from "react";
import {
  addTikTokVideo,
  deleteTikTokVideo,
  reorderTikTokVideos,
} from "../firebase/firebase-functions";
import { FaTrash, FaArrowUp, FaArrowDown, FaPlus } from "react-icons/fa";

const TikTokManagement = ({ videos, onUpdate }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [embedCode, setEmbedCode] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState("");

  const handleAddVideo = async (e) => {
    e.preventDefault();

    if (!embedCode.trim()) {
      setError("Please paste the TikTok embed code");
      return;
    }

    // Check if already at max videos
    if (videos.length >= 6) {
      setError("Maximum 6 videos allowed");
      return;
    }

    // Validate that it's TikTok embed code
    if (
      !embedCode.includes("tiktok-embed") ||
      !embedCode.includes("blockquote")
    ) {
      setError(
        "Invalid embed code. Please use the code from TikTok's embed tool.",
      );
      return;
    }

    // Extract video URL from embed code
    const urlMatch = embedCode.match(/cite="([^"]+)"/);
    const videoUrl = urlMatch ? urlMatch[1] : "";

    if (!videoUrl) {
      setError("Could not find video URL in embed code");
      return;
    }

    setIsAdding(true);
    setError("");

    try {
      const result = await addTikTokVideo({
        url: videoUrl,
        embedHtml: embedCode.trim(),
        order: videos.length + 1,
        isActive: true,
      });

      if (result.success) {
        setEmbedCode("");
        setShowAddForm(false);
        onUpdate();
        alert("Video added successfully!");
      } else {
        setError(result.error || "Failed to add video");
      }
    } catch (err) {
      setError("Failed to add video");
      console.error(err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!confirm("Are you sure you want to delete this video?")) {
      return;
    }

    try {
      const result = await deleteTikTokVideo(videoId);

      if (result.success) {
        onUpdate();
      } else {
        alert("Failed to delete video: " + result.error);
      }
    } catch (err) {
      alert("Failed to delete video");
      console.error(err);
    }
  };

  const handleReorder = async (index, direction) => {
    const newVideos = [...videos];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    // Swap videos
    [newVideos[index], newVideos[targetIndex]] = [
      newVideos[targetIndex],
      newVideos[index],
    ];

    try {
      const result = await reorderTikTokVideos(newVideos);

      if (result.success) {
        onUpdate();
      } else {
        alert("Failed to reorder videos: " + result.error);
      }
    } catch (err) {
      alert("Failed to reorder videos");
      console.error(err);
    }
  };

  return (
    <div className="tiktok-management">
      <div className="section-header">
        <h3>TikTok Videos</h3>
        <div className="header-actions">
          <span style={{ marginRight: "10px" }} className="video-counter">
            {videos.length}/6 videos
          </span>
          {videos.length < 6 && (
            <button
              className="btn-primary"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <FaPlus /> Add Video
            </button>
          )}
        </div>
      </div>

      {/* Add Video Form */}
      {showAddForm && (
        <div className="add-video-form">
          <h4>Add TikTok Video</h4>

          {/* Instructions */}
          <div className="embed-instructions">
            <p>
              <strong>How to get the embed code:</strong>
            </p>
            <ol>
              <li>
                Go to{" "}
                <a
                  href="https://www.tiktok.com/embed"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  TikTok Embed Tool
                </a>
              </li>
              <li>Paste your TikTok video URL in the "URL" field</li>
              <li>Click "Get code"</li>
              <li>
                Copy the entire code (the{" "}
                <code>&lt;blockquote&gt;...&lt;/blockquote&gt;</code> section)
              </li>
              <li>Paste it below</li>
            </ol>
          </div>

          <form onSubmit={handleAddVideo}>
            <div className="form-group">
              <label htmlFor="embedCode">Embed Code</label>
              <textarea
                id="embedCode"
                value={embedCode}
                onChange={(e) => setEmbedCode(e.target.value)}
                placeholder='Paste the TikTok embed code here (starts with <blockquote class="tiktok-embed"...)'
                rows="8"
                disabled={isAdding}
                required
              />
            </div>

            {error && <p className="error-message">{error}</p>}

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={isAdding}>
                {isAdding ? "Adding..." : "Add Video"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setEmbedCode("");
                  setError("");
                }}
                disabled={isAdding}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Max Videos Notice */}
      {videos.length >= 6 && (
        <div className="max-videos-notice">
          <p>Maximum of 6 videos reached. Delete a video to add a new one.</p>
        </div>
      )}

      {/* Videos List */}
      <div className="videos-list">
        {videos.length === 0 ? (
          <div className="empty-state">
            <p>No TikTok videos yet. Add your first video!</p>
          </div>
        ) : (
          videos.map((video, index) => (
            <div key={video.id} className="video-item">
              <div className="video-order">#{index + 1}</div>

              <div className="video-info">
                <a
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="video-url"
                >
                  {video.url}
                </a>
                {video.title && <p className="video-title">{video.title}</p>}
                <span
                  className={`video-status ${
                    video.isActive ? "active" : "inactive"
                  }`}
                >
                  {video.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="video-actions">
                {/* Reorder buttons */}
                <button
                  className="icon-btn"
                  onClick={() => handleReorder(index, "up")}
                  disabled={index === 0}
                  title="Move up"
                >
                  <FaArrowUp />
                </button>
                <button
                  className="icon-btn"
                  onClick={() => handleReorder(index, "down")}
                  disabled={index === videos.length - 1}
                  title="Move down"
                >
                  <FaArrowDown />
                </button>

                {/* Delete button */}
                <button
                  className="icon-btn delete-btn"
                  onClick={() => handleDelete(video.id)}
                  title="Delete video"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TikTokManagement;
